import { ThisReceiver } from '@angular/compiler';
import { ApplicationRef, Component, ElementRef, Inject, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HotTableRegisterer } from '@handsontable/angular';
import Handsontable from 'handsontable';
import { toNumber } from 'lodash';
import { Utils } from '../utils';
import * as THREE from 'three'; // Import THREE

// Drawing tool types
enum DrawingTool {
  SELECT = 'select',
  LINE = 'line',
  SPLINE = 'spline',
  CIRCLE = 'circle'
}

// Selection modes for move tool
enum SelectionMode {
  POINT = 'point',
  SEGMENT = 'segment',
  SHAPE = 'shape'
}

// Shape segment types for mixed line/spline shapes
interface ShapeSegment {
  type: 'line' | 'spline' | 'circle';
  points: ShapePoint[];
  id: string;
  splineDivisions?: number;
  radius?: number; // For circles
  center?: ShapePoint; // For circles
}

interface ShapePoint {
  cx: number;
  cy: number;
}

interface ShapeObject {
  type: 'main' | 'hole';
  points: ShapePoint[];
  segments?: ShapeSegment[]; // New: for mixed line/spline shapes (optional for backward compatibility)
  id: string; // Unique ID for ngFor tracking, e.g., 'main' or 'hole-1', 'hole-2'
  splineDivisions?: number; // Optional: Number of divisions for the spline curve
  shapeType?: 'polygon' | 'circle'; // New: to distinguish between polygon and circle shapes
  radius?: number; // For circle shapes
  center?: ShapePoint; // For circle shapes
}

@Component({
  selector: 'app-dialogshape',
  templateUrl: './dialogshape.component.html',
  styleUrls: ['./dialogshape.component.scss']
  // standalone: true, // REMOVE standalone
  // imports: [] // REMOVE imports array
})
export class DialogshapeComponent implements OnInit, AfterViewInit, OnDestroy {

  shapes: ShapeObject[] = [];
  currentShapeIndex = 0; // Index of the currently active shape in the 'shapes' array
  currentPointIndex = -1; // Index of the selected point in the current shape's 'points' array

  // Drawing tools
  currentTool: DrawingTool = DrawingTool.SELECT;
  isDrawing = false;
  drawingStartPoint: ShapePoint | null = null;
  tempDrawingGeometry: THREE.Line | THREE.Mesh | null = null;
  previewLine: THREE.Line | null = null;
  previewCircle: THREE.Line | null = null; // Add preview circle for circle tool
  lastClickTime = 0;
  doubleClickThreshold = 300; // milliseconds
  startPointMesh: THREE.Mesh | null = null; // Visual indicator for start point
  snapIndicator: THREE.Mesh | null = null; // Visual indicator for snap points

  // Move tool properties
  selectionMode: SelectionMode = SelectionMode.POINT;
  selectedSegmentIndex: number = -1;
  selectedShapeForMove: number = -1;
  selectedPoints: number[] = [];
  isMovingObject = false;
  moveStartPosition: THREE.Vector3 | null = null;
  boundingBoxHelper: THREE.BoxHelper | null = null;
  selectionBounds: THREE.Box3 | null = null;
  moveGhostMeshes: THREE.Mesh[] = []; // Ghost points showing original position during move
  pointMeshes: THREE.Mesh[] = []; // Stores all created point mesh instances
  private scenePointMeshesForRaycasting: THREE.Mesh[] = []; // Stores point meshes of the current shape for interaction
  shapeLines: THREE.Line[] = [];
  splineVisualizationLines: THREE.Line[] = []; // Stores visualized splines
  private hoveredPointMesh: THREE.Mesh | null = null; // For mouseover effect

  // Rectangle selection properties
  isRectangleSelecting = false;
  rectangleSelectionStart: THREE.Vector2 | null = null;
  rectangleSelectionEnd: THREE.Vector2 | null = null;
  selectionRectangleMesh: THREE.Mesh | null = null;
  multiSelectDragStart: { [pointIndex: number]: THREE.Vector3 } = {}; // Store original positions for multi-select drag


  value1 = ""; // This will store the final string representation for output

  // dragStartOffset = { x: 0, y: 0 }; // Will be 3D
  dragStartWorldPos: THREE.Vector3 | null = null;
  selectedPointMesh: THREE.Mesh | null = null;


  mdown = false;
  copytable = false;
  cellref = "D10";
  private hotRegisterer = new HotTableRegisterer();
  public hot: Handsontable;
  hotid = 'hotInstance1';
  @ViewChild("hotContainer1") hotContainer: ElementRef;
  @ViewChild('threeCanvas', { static: true }) private threeCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('threeContainer', { static: true }) private threeContainer: ElementRef<HTMLDivElement>;


  // Three.js specific properties
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private renderer: THREE.WebGLRenderer;
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;
  private plane: THREE.Plane; // For raycasting intersections
  private gridHelper: THREE.GridHelper;
  private ambientLight: THREE.AmbientLight;
  private directionalLight: THREE.DirectionalLight;

  // Panning and Zoom for Three.js
  isPanning3D = false;
  lastMousePos3D = { x: 0, y: 0 };
  zoomLevel = 30; // Initial zoom level, smaller means more zoomed in for orthographic
  panOffset3D = new THREE.Vector3(0, 0, 0);

  snap = true;
  gridsize = 10; // This will be used for snapping logic and possibly grid visualization
  cornerRadius = 10; // Default radius for rounded corners

  constructor(public dialogRef: MatDialogRef<any>, @Inject(MAT_DIALOG_DATA) public data: any, public appref: ApplicationRef) {
    this.value1 = JSON.parse(JSON.stringify(data.value || '')); // Ensure data.value is not null
    this.parseInitialValue(this.value1);
    if (this.shapes.length === 0) { // Ensure there's at least a main shape
      this.shapes.push({
        type: 'main',
        points: [],
        segments: [],
        id: 'main',
        splineDivisions: 30,
        shapeType: 'polygon'
      });
    }
  }

  ngOnInit(): void {
    // document.addEventListener('contextmenu', (e) => e.preventDefault()); // Keep for now
    // this.gridchange(); // Grid will be handled by Three.js
    this.updateData(); // Initial update for Handsontable

    // Add keyboard shortcuts
    document.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  ngAfterViewInit(): void {
    this.initThreeJS();
    this.renderScene(); // Initial render

    if (this.hotContainer?.nativeElement) {
      try {

        this.hotRegisterer.getInstance(this.hotid)?.destroy(); // Destroy existing instance if any
      } catch (e) { }

      this.hot = new Handsontable(this.hotContainer.nativeElement, {
        data: this.dataset,
        rowHeaders: true,
        colHeaders: ['x', 'y'], // Later, consider adding 'z' if shapes become 3D
        manualColumnResize: true,
        autoColumnSize: true,
        colWidths: 80,
        height: 'auto',
        licenseKey: 'non-commercial-and-evaluation',
        afterChange: (changes, source) => this.afterChange(changes, source),
        afterSelection: (r, c, r2, c2) => this.cellSelected(r, c, r2, c2),
        contextMenu: true,
        fillHandle: true,
        outsideClickDeselects: false,
      });
    }
    this.updateAndRenderThreeObjects(); // Create initial 3D objects
  }

  private initThreeJS(): void {
    const width = this.threeContainer.nativeElement.clientWidth;
    const height = this.threeContainer.nativeElement.clientHeight;

    // Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf0f0f0);

    // Camera (Orthographic)
    const aspect = width / height;
    // Larger view size means more "zoomed out"
    const viewSize = 200; // This will be controlled by zoomLevel
    this.camera = new THREE.OrthographicCamera(
      -aspect * viewSize / 2, // left
      aspect * viewSize / 2,  // right
      viewSize / 2,           // top
      -viewSize / 2,          // bottom
      1,                      // near
      1000                    // far
    );
    this.camera.position.set(0, 0, 500); // Position the camera
    this.camera.lookAt(this.scene.position);
    // Rotate the camera 180 degrees around Z axis for default view
    this.camera.rotation.z = Math.PI;


    // Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas: this.threeCanvas.nativeElement, antialias: true });
    this.renderer.setSize(width, height);

    // Raycaster and Mouse Vector
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0); // XY plane for 2D-like interaction

    // Lights
    this.ambientLight = new THREE.AmbientLight(0x404040, 2); // soft white light
    this.scene.add(this.ambientLight);
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    this.directionalLight.position.set(5, 5, 5).normalize();
    this.scene.add(this.directionalLight);

    // Grid Helper
    this.gridHelper = new THREE.GridHelper(1000, 20, 0x888888, 0xcccccc); // size, divisions, colorCenterLine, colorGrid
    this.gridHelper.rotation.x = Math.PI / 2; // Rotate to be on XY plane
    this.scene.add(this.gridHelper);

    // Axes Helper
    const axesHelper = new THREE.AxesHelper(500); // Length of axes lines
    this.scene.add(axesHelper);


    // Initial zoom update for camera
    this.updateCameraProjection();
  }

  private updateCameraProjection(): void {
    if (!this.camera || !this.threeContainer) return;
    const aspect = this.threeContainer.nativeElement.clientWidth / this.threeContainer.nativeElement.clientHeight;
    // zoomLevel inversely relates to viewSize. Smaller zoomLevel means larger viewSize (zoomed out).
    // Let's adjust interpretation: higher zoomLevel means more zoomed in (smaller viewSize).
    // For example, if zoomLevel is 50, viewSize might be 200. If zoomLevel is 100 (more zoomed in), viewSize might be 100.
    // Let's use a base view size and scale it by zoomLevel.
    // Let baseViewSize be 400. zoomLevel of 1 means baseViewSize. zoomLevel of 2 means baseViewSize / 2.
    const baseViewSize = 400; // Adjust as needed for good initial view
    const viewSize = baseViewSize / (this.zoomLevel / 50); // Normalize zoomLevel around a sensible default like 50

    this.camera.left = -aspect * viewSize / 2 + this.panOffset3D.x;
    this.camera.right = aspect * viewSize / 2 + this.panOffset3D.x;
    this.camera.top = viewSize / 2 + this.panOffset3D.y;
    this.camera.bottom = -viewSize / 2 + this.panOffset3D.y;
    this.camera.updateProjectionMatrix();
    this.renderScene();
  }


  private renderScene(): void {
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }


  parseInitialValue(value: string) {
    this.shapes = [];
    if (!value) {
      this.shapes.push({
        type: 'main',
        points: [{ cx: 100, cy: 100 }, { cx: 150, cy: 25 }, { cx: 150, cy: 75 }, { cx: 200, cy: 0 }],
        segments: [],
        id: 'main',
        splineDivisions: 30,
        shapeType: 'polygon'
      });
      // Add a test hole shape
      this.shapes.push({
        type: 'hole',
        points: [{ cx: 120, cy: 60 }, { cx: 140, cy: 40 }, { cx: 130, cy: 50 }],
        segments: [],
        id: 'hole-test',
        splineDivisions: 30,
        shapeType: 'polygon'
      });
      this.currentShapeIndex = 0;
      return;
    }

    const shapeStrings = value.split('#');
    shapeStrings.forEach((shapeStr, index) => {
      const points: ShapePoint[] = [];
      const pointTokens = shapeStr.split(';');
      if (pointTokens.length === 1 && pointTokens[0] === '') {
        // if (index === 0) { } // Main shape can be empty
      } else {
        pointTokens.forEach(token => {
          if (token) {
            const p = token.replace("(", "").replace(")", "").split(",");
            if (p.length === 2) {
              points.push({ cx: parseInt(p[0], 10) || 0, cy: parseInt(p[1], 10) || 0 });
            }
          }
        });
      }

      this.shapes.push({
        type: index === 0 ? 'main' : 'hole',
        points: points,
        segments: [],
        id: `${index === 0 ? 'main' : 'hole'}-${Date.now()}-${index}`,
        splineDivisions: 30, // Default spline divisions
        shapeType: 'polygon'
      });
    });

    if (this.shapes.length === 0) {
      this.shapes.push({
        type: 'main',
        points: [{ cx: 100, cy: 100 }],
        segments: [],
        id: 'main-fallback',
        splineDivisions: 30,
        shapeType: 'polygon'
      });
    }
    this.currentShapeIndex = 0;
  }

  afterChange = async (changes: Handsontable.CellChange[], source: Handsontable.ChangeSource) => {
    if (source === 'loadData' || !changes) {
      return;
    }
    try {
      changes.forEach(change => {
        const [row, prop, oldValue, newValue] = change;
        const currentShape = this.shapes[this.currentShapeIndex];
        if (currentShape && currentShape.points[row]) {
          const point = currentShape.points[row];
          if (prop === 0 || prop === '0') { // cx
            point.cx = toNumber(newValue);
          }
          if (prop === 1 || prop === '1') { // cy
            point.cy = toNumber(newValue);
          }
        }
      });
      this.updateData(); // This will also trigger 3D updates
    } catch (err) {
      console.log('Error in afterChange:', err);
    }
  }

  currow = null;
  cellSelected = async (r: number, c: number, r2: number, c2: number) => {
    this.currentPointIndex = r;
    this.currow = r;

    // Update the selectedPointMesh based on table selection
    this.selectedPointMesh = this.pointMeshes.find(m =>
      m.userData.shapeIndex === this.currentShapeIndex && m.userData.pointIndex === r
    ) || null;

    // Highlight corresponding point in 3D scene
    this.highlightSelectedPointMesh(r);
    this.renderScene();
  }

  dataset = [];
  // polylines: { points: string, type: 'main' | 'hole', id: string }[] = []; // SVG specific, removed

  // svgMouseDown(event: MouseEvent) { // Replaced by Three.js mousedown
  //   if (event.button === 2) { // Right mouse button for panning
  //     this.isPanning3D = true;
  //     this.lastMousePos3D = { x: event.clientX, y: event.clientY };
  //     event.preventDefault();
  //   }
  // }

  // mousedown(event: MouseEvent, pointIndex: number) { // Replaced by Three.js mousedown (part of svgMouseDown now)
  //   event.stopPropagation();
  //   // Logic will be handled in canvas mousedown
  // }

  svgMouseDown(event: MouseEvent): void { // Renamed from canvasMouseDown for HTML binding
    event.preventDefault();
    // console.log('Mouse down, current tool:', this.currentTool, 'button:', event.button);

    this.mouse.x = (event.offsetX / this.threeContainer.nativeElement.clientWidth) * 2 - 1;
    this.mouse.y = -(event.offsetY / this.threeContainer.nativeElement.clientHeight) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);

    if (event.button === 0) { // Left mouse button
      const intersectionPointWorld = new THREE.Vector3();
      const hasIntersection = this.raycaster.ray.intersectPlane(this.plane, intersectionPointWorld);

      if (this.currentTool === DrawingTool.SELECT && hasIntersection) {
        const currentTime = Date.now();
        const isDoubleClick = (currentTime - this.lastClickTime) < this.doubleClickThreshold;
        const pointIntersects = this.raycaster.intersectObjects(this.scenePointMeshesForRaycasting);

        if (isDoubleClick && pointIntersects.length === 0) {
          this.addPointBetweenNearest(intersectionPointWorld);
          this.lastClickTime = 0;
          return;
        }
        this.lastClickTime = currentTime;
      }

      if (this.currentTool !== DrawingTool.SELECT && hasIntersection) {
        const handled = this.handleDrawingMouseDown(intersectionPointWorld);
        if (handled) return;
      }

      const intersects = this.raycaster.intersectObjects(this.scenePointMeshesForRaycasting);

      if (intersects.length > 0) {
        const clickedMesh = intersects[0].object as THREE.Mesh;
        const clickedPointIndex = clickedMesh.userData.pointIndex;
        const clickedShapeIndex = clickedMesh.userData.shapeIndex;

        if (clickedShapeIndex !== this.currentShapeIndex) {
          this.selectShape(clickedShapeIndex);
        }

        const isCtrlOrCmd = event.ctrlKey || event.metaKey;
        let dragInitiated = false;

        if (isCtrlOrCmd) {
          const existingIdx = this.selectedPoints.indexOf(clickedPointIndex);
          if (existingIdx >= 0) {
            this.selectedPoints.splice(existingIdx, 1);
            if (clickedPointIndex === this.currentPointIndex) {
              this.currentPointIndex = this.selectedPoints.length > 0 ? this.selectedPoints[0] : -1;
              this.selectedPointMesh = this.currentPointIndex >= 0 ?
                this.pointMeshes.find(m => m.userData.shapeIndex === this.currentShapeIndex && m.userData.pointIndex === this.currentPointIndex) || null : null;
            }
          } else {
            this.selectedPoints.push(clickedPointIndex);
            if (this.currentPointIndex === -1 || this.selectedPoints.length === 1) {
              this.currentPointIndex = clickedPointIndex;
              this.selectedPointMesh = clickedMesh;
            }
          }
          if (this.selectedPointMesh) { // Allow drag if a primary point is set
            dragInitiated = true;
          }
        } else { // Not Ctrl/Cmd click
          if (this.selectedPoints.includes(clickedPointIndex)) {
            // Clicked on an already selected point (possibly part of a multi-selection)
            // Make this the primary point for dragging the group
            this.currentPointIndex = clickedPointIndex;
            this.selectedPointMesh = clickedMesh;
            dragInitiated = true;
          } else {
            // Clicked a new point, not part of current selection - start new selection
            this.selectedPoints = [clickedPointIndex];
            this.currentPointIndex = clickedPointIndex;
            this.selectedPointMesh = clickedMesh;
            dragInitiated = true;
          }
        }

        if (dragInitiated && this.selectedPointMesh) {
          this.mdown = true;
          // Use intersectionPointWorld which is already calculated for the plane
          this.dragStartWorldPos = intersectionPointWorld.clone().sub(this.selectedPointMesh.position);

          this.multiSelectDragStart = {};
          const currentShape = this.shapes[this.currentShapeIndex];
          if (currentShape) {
            this.selectedPoints.forEach(idx => {
              if (currentShape.points[idx]) {
                this.multiSelectDragStart[idx] = new THREE.Vector3(currentShape.points[idx].cx, currentShape.points[idx].cy, 0);
              }
            });
            // Ensure the currentPointIndex (if valid and not already added) is in multiSelectDragStart
            // This is crucial if currentPointIndex was set but not yet in selectedPoints during a complex sequence.
            // However, by this point, selectedPoints should be the definitive list.
            if (this.currentPointIndex !== -1 && currentShape.points[this.currentPointIndex] && !this.multiSelectDragStart[this.currentPointIndex]) {
              // This case should ideally not be hit if selectedPoints is managed correctly before this.
              // console.warn('currentPointIndex was not in multiSelectDragStart, adding it.');
              // this.multiSelectDragStart[this.currentPointIndex] = new THREE.Vector3(currentShape.points[this.currentPointIndex].cx, currentShape.points[this.currentPointIndex].cy, 0);
            }
          }
        }

        if (this.hot && this.currentPointIndex !== -1) {
          this.hot.selectCell(this.currentPointIndex, 0, this.currentPointIndex, 1, false, false);
        } else if (this.hot) {
          this.hot.deselectCell();
        }
        this.highlightSelectedPointMesh(this.currentPointIndex);

      } else { // Clicked on empty space
        if (this.currentTool === DrawingTool.SELECT && hasIntersection) {
          this.startRectangleSelection(intersectionPointWorld);
        } else {
          this.selectedPointMesh = null;
          this.currentPointIndex = -1;
          this.selectedPoints = [];
          if (this.hot) this.hot.deselectCell();
          this.highlightSelectedPointMesh(-1);
        }
      }
    } else if (event.button === 2) {
      this.isPanning3D = true;
      this.lastMousePos3D = { x: event.clientX, y: event.clientY };
    }
  }


  mouseup(event: MouseEvent) {
    this.mdown = false;
    this.isPanning3D = false;

    // Handle rectangle selection end
    if (this.isRectangleSelecting) {
      this.finishRectangleSelection();
      return;
    }

    // Clear ghost points when movement ends
    if (this.isMovingObject) {
      this.clearGhostPoints();
    }

    this.isMovingObject = false;
    this.moveStartPosition = null;
    this.multiSelectDragStart = {}; // Clear multi-select drag data
  }

  mousemove(event: MouseEvent) {
    if (this.isPanning3D) {
      const deltaX = event.clientX - this.lastMousePos3D.x;
      const deltaY = event.clientY - this.lastMousePos3D.y;

      // Convert pixel pan to world units based on current zoom
      const worldDeltaX = (deltaX / this.threeContainer.nativeElement.clientWidth) * (this.camera.right - this.camera.left);
      const worldDeltaY = (deltaY / this.threeContainer.nativeElement.clientHeight) * (this.camera.top - this.camera.bottom);

      this.panOffset3D.x -= worldDeltaX;
      this.panOffset3D.y += worldDeltaY; // Mouse Y is typically inverted

      this.lastMousePos3D = { x: event.clientX, y: event.clientY };
      this.updateCameraProjection();
      return;
    }

    this.mouse.x = (event.offsetX / this.threeContainer.nativeElement.clientWidth) * 2 - 1;
    this.mouse.y = -(event.offsetY / this.threeContainer.nativeElement.clientHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Handle preview line update for line tool
    if (this.isDrawing && this.currentTool === DrawingTool.LINE && this.drawingStartPoint) {
      const intersectionPoint = new THREE.Vector3();
      if (this.raycaster.ray.intersectPlane(this.plane, intersectionPoint)) {
        let previewEndPoint = { cx: intersectionPoint.x, cy: intersectionPoint.y };

        // Handle snapping and show snap indicator
        if (this.snap) {
          const snappedX = Math.round(previewEndPoint.cx / this.gridsize) * this.gridsize;
          const snappedY = Math.round(previewEndPoint.cy / this.gridsize) * this.gridsize;

          // Show snap indicator if position changed due to snapping
          if (Math.abs(previewEndPoint.cx - snappedX) > 0.1 || Math.abs(previewEndPoint.cy - snappedY) > 0.1) {
            this.addSnapIndicator({ cx: snappedX, cy: snappedY });
          } else {
            this.clearSnapIndicator();
          }

          previewEndPoint.cx = snappedX;
          previewEndPoint.cy = snappedY;
        } else {
          this.clearSnapIndicator();
        }

        this.updatePreviewLine(this.drawingStartPoint, previewEndPoint);
      }
    }
    // Handle preview circle update for circle tool
    else if (this.isDrawing && this.currentTool === DrawingTool.CIRCLE && this.drawingStartPoint) {
      const intersectionPoint = new THREE.Vector3();
      if (this.raycaster.ray.intersectPlane(this.plane, intersectionPoint)) {
        let previewEndPoint = { cx: intersectionPoint.x, cy: intersectionPoint.y };

        // Handle snapping for circle edge point
        if (this.snap) {
          const snappedX = Math.round(previewEndPoint.cx / this.gridsize) * this.gridsize;
          const snappedY = Math.round(previewEndPoint.cy / this.gridsize) * this.gridsize;

          // Show snap indicator if position changed due to snapping
          if (Math.abs(previewEndPoint.cx - snappedX) > 0.1 || Math.abs(previewEndPoint.cy - snappedY) > 0.1) {
            this.addSnapIndicator({ cx: snappedX, cy: snappedY });
          } else {
            this.clearSnapIndicator();
          }

          previewEndPoint.cx = snappedX;
          previewEndPoint.cy = snappedY;
        } else {
          this.clearSnapIndicator();
        }

        // Calculate radius and update preview circle
        const radius = Math.sqrt(
          Math.pow(previewEndPoint.cx - this.drawingStartPoint.cx, 2) +
          Math.pow(previewEndPoint.cy - this.drawingStartPoint.cy, 2)
        );

        this.updatePreviewCircle(this.drawingStartPoint, radius);
      }
    }
    // Handle rectangle selection update
    else if (this.isRectangleSelecting && this.rectangleSelectionStart) {
      const intersectionPoint = new THREE.Vector3();
      if (this.raycaster.ray.intersectPlane(this.plane, intersectionPoint)) {
        this.rectangleSelectionEnd = new THREE.Vector2(intersectionPoint.x, intersectionPoint.y);
        this.updateSelectionRectangle();
      }
    }
    // Handle individual point dragging (for select tool)
    else if (this.mdown && this.selectedPointMesh && this.currentPointIndex > -1) {
      // Check if we're dragging multiple selected points
      if (this.selectedPoints.length > 1) {
        this.handleMultiPointDrag();
      } else {
        // Single point dragging logic
        const intersectionPoint = new THREE.Vector3();
        if (this.raycaster.ray.intersectPlane(this.plane, intersectionPoint)) {
          let newPos = intersectionPoint.clone();
          if (this.dragStartWorldPos) {
            newPos.sub(this.dragStartWorldPos);
          }

          const currentShape = this.shapes[this.currentShapeIndex];
          if (currentShape && currentShape.points[this.currentPointIndex]) {
            let targetX = newPos.x;
            let targetY = newPos.y;

            if (this.snap) {
              targetX = Math.round(newPos.x / this.gridsize) * this.gridsize;
              targetY = Math.round(newPos.y / this.gridsize) * this.gridsize;
            }

            // Check if moving to this position would create a duplicate
            const TOLERANCE = this.snap ? this.gridsize * 0.1 : 0.001; // Larger tolerance when snapping
            const wouldBeDuplicate = currentShape.points.some((p, idx) => {
              if (idx === this.currentPointIndex) return false; // Skip the point being moved
              const distance = Math.sqrt(
                Math.pow(p.cx - targetX, 2) +
                Math.pow(p.cy - targetY, 2)
              );
              return distance < TOLERANCE;
            });

            if (wouldBeDuplicate) {
              // Don't update - keep the point at its current position
              return;
            }

            // Update coordinates directly since camera is rotated 180 degrees
            currentShape.points[this.currentPointIndex].cx = targetX;
            currentShape.points[this.currentPointIndex].cy = targetY;

            // Update the 3D mesh position directly (already in display coordinates)
            this.selectedPointMesh.position.set(targetX, targetY, 0);

            this.updateData(); // This will update handsontable and re-render lines
          }
        }
      }
    } else if (!this.mdown) { // Mouse hover logic (not dragging, not panning)
      const intersects = this.raycaster.intersectObjects(this.scenePointMeshesForRaycasting);
      if (intersects.length > 0) {
        const firstIntersectedObject = intersects[0].object as THREE.Mesh;
        if (firstIntersectedObject.userData.type === 'point') {
          if (this.hoveredPointMesh !== firstIntersectedObject) {
            const oldHovered = this.hoveredPointMesh;
            this.hoveredPointMesh = firstIntersectedObject;

            // Debug logging
            console.log(`Hovering over point:`, {
              shapeIndex: firstIntersectedObject.userData.shapeIndex,
              shapeType: firstIntersectedObject.userData.shapeType,
              pointIndex: firstIntersectedObject.userData.pointIndex,
              position: firstIntersectedObject.position
            });

            // Restore old hovered point color
            if (oldHovered) {
              const oldShape = this.shapes[oldHovered.userData.shapeIndex];
              const oldIsCurrentShape = oldHovered.userData.shapeIndex === this.currentShapeIndex;
              let oldColor: number;

              if (!oldIsCurrentShape) {
                oldColor = 0x666666; // Gray
              } else {
                // Check if it was selected
                if (this.selectedPointMesh === oldHovered && oldHovered.userData.pointIndex === this.currentPointIndex) {
                  oldColor = 0x00ff00; // Green for selected
                } else {
                  oldColor = oldShape?.type === 'main' ? 0x0000ff : 0xff0000; // Blue/Red
                }
              }

              console.log(`Restoring old hover color to 0x${oldColor.toString(16)}`);
              (oldHovered.material as THREE.MeshBasicMaterial).color.setHex(oldColor);
            }

            // Set new hover color
            console.log(`Setting hover color to yellow (0xffff00)`, firstIntersectedObject, intersects.length);
            (firstIntersectedObject.material as THREE.MeshBasicMaterial).color.setHex(0xffff00);
            this.renderScene();
          }
        } else { // Intersected something else, or nothing of interest
          if (this.hoveredPointMesh !== null) {
            console.log(`Clearing hover1`, this.hoveredPointMesh);
            this.hoveredPointMesh = null;
            this.highlightSelectedPointMesh(this.currentPointIndex); // Re-evaluate highlights
          }
        }
      } else {
        if (this.hoveredPointMesh !== null) {
          console.log(`Clearing hover`, this.hoveredPointMesh); console.log(`Clearing hover`, this.hoveredPointMesh);
          // Clear hover
          const oldHovered = this.hoveredPointMesh;
          this.hoveredPointMesh = null;

          // Force update the previously hovered mesh
          if (oldHovered) {
            const shape = this.shapes[oldHovered.userData.shapeIndex];
            const isCurrentShape = oldHovered.userData.shapeIndex === this.currentShapeIndex;
            const baseColor = isCurrentShape ? (shape?.type === 'main' ? 0x0000ff : 0xff0000) : 0x666666;
            (oldHovered.material as THREE.MeshBasicMaterial).color.setHex(baseColor);
          }

          this.highlightSelectedPointMesh(this.currentPointIndex); // Re-evaluate highlights
          this.renderScene();
        }
      }
    }
  }

  gridchange() {
    if (this.scene && this.gridHelper) {
      this.scene.remove(this.gridHelper); // Remove old grid
      // Ensure gridsize is positive to avoid issues with divisions
      const newGridSize = Math.max(1, this.gridsize);
      const divisions = Math.floor(1000 / newGridSize); // Keep total size 1000, adjust divisions
      this.gridHelper = new THREE.GridHelper(1000, divisions, 0x888888, 0xcccccc);
      this.gridHelper.rotation.x = Math.PI / 2; // Rotate to be on XY plane
      this.gridHelper.rotation.z = Math.PI; // Flip grid to match flipped axes
      this.scene.add(this.gridHelper);
      this.renderScene();
    }
  }

  updateData() {
    try {
      const activeShape = this.shapes[this.currentShapeIndex];
      if (activeShape) {
        this.dataset = activeShape.points.map(p => [p.cx, p.cy]);
      } else {
        this.dataset = [];
      }

      if (this.hot) {
        this.hot.loadData(this.dataset);
      }

      this.value1 = this.shapes.map(shape =>
        shape.points.map(p => `(${p.cx},${p.cy})`).join(';')
      ).join('#');

      if (this.copytable && activeShape && activeShape.points.length > 0) {
        let tempValue = '="';
        const ij = Utils.cellname2indices(this.cellref);
        activeShape.points.forEach((p, i) => {
          const namex = Utils.indices2cellaname(ij[0], ij[1] + i);
          const namey = Utils.indices2cellaname(ij[0] + 1, ij[1] + i);
          tempValue += `(""${namex}"",""${namey}"");`;
        });
        if (tempValue.endsWith(';')) {
          tempValue = tempValue.slice(0, -1);
        }
        tempValue += '"';
        this.value1 = tempValue;
      }

      // Only attempt to update 3D objects if the scene has been initialized
      if (this.scene) {
        this.updateAndRenderThreeObjects();
      }

    } catch (err) {
      console.error('Error in DialogshapeComponent.updateData:', err);
    }
  }


  private updateAndRenderThreeObjects(): void {
    if (!this.scene || !this.camera) { // Guard against uninitialized scene/camera
      // console.warn("updateAndRenderThreeObjects skipped: Scene or Camera not initialized.");
      return;
    }

    // Clear ALL point meshes from scene and raycasting list (not just current shape)
    this.pointMeshes.forEach(mesh => {
      this.scene.remove(mesh);
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) (mesh.material as THREE.Material).dispose();
    });
    this.scenePointMeshesForRaycasting = [];

    // Clear all previously created point mesh references (they will be recreated)
    this.pointMeshes = [];

    // Clear previously drawn shape lines from the scene
    this.shapeLines.forEach(line => this.scene.remove(line));
    this.shapeLines = [];

    // Clear previously drawn spline visualization lines
    this.splineVisualizationLines.forEach(line => this.scene.remove(line));
    this.splineVisualizationLines = [];

    const pointGeometry = new THREE.BoxGeometry(8, 8, 8); // Increased point size

    this.shapes.forEach((shape, shapeIdx) => {
      const isCurrentShape = shapeIdx === this.currentShapeIndex;
      const pointsForLine: THREE.Vector3[] = [];

      // console.log(`Creating points for shape ${shapeIdx} (${shape.type}), is current: ${isCurrentShape}, points: ${shape.points.length}`);

      shape.points.forEach((p, pointIdx) => {
        // Set initial color based on shape type and whether it's current
        let initialColor: number;
        if (!isCurrentShape) {
          initialColor = 0x666666; // Gray for inactive shapes (both main and hole)
        } else {
          initialColor = shape.type === 'main' ? 0x0000ff : 0xff0000; // Blue for main, Red for hole (active shape)
        }

        const pointMaterial = new THREE.MeshBasicMaterial({ color: initialColor });
        const pointMesh = new THREE.Mesh(pointGeometry, pointMaterial);
        pointMesh.position.set(p.cx, p.cy, 0);
        pointMesh.userData = { shapeIndex: shapeIdx, pointIndex: pointIdx, type: 'point', shapeType: shape.type };

        // console.log(`Created ${shape.type} point ${pointIdx} at (${p.cx}, ${p.cy}) with color 0x${initialColor.toString(16)}`);

        this.pointMeshes.push(pointMesh);
        this.scene.add(pointMesh);
        this.scenePointMeshesForRaycasting.push(pointMesh);
        pointsForLine.push(new THREE.Vector3(p.cx, p.cy, 0));
      });

      if (pointsForLine.length > 1) {
        // For closed shapes, add the first point again to the end
        if (shape.type === 'main' || shape.type === 'hole') { // Assuming holes are also closed loops
          if (pointsForLine.length > 0) pointsForLine.push(pointsForLine[0].clone());
        }
        const lineMaterialColor = isCurrentShape ? (shape.type === 'main' ? 0x0000cc : 0xcc0000) : 0xaaaaaa;
        const lineMaterial = new THREE.LineBasicMaterial({ color: lineMaterialColor, linewidth: 2 }); // linewidth might not work on all platforms
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(pointsForLine);
        const line = new THREE.Line(lineGeometry, lineMaterial);
        this.shapeLines.push(line);
        this.scene.add(line);
      }

      // Visualize the Catmull-Rom spline for the current shape if it has enough points
      if (shape.points.length >= 2) {
        const splineControlPoints = shape.points.map(p => new THREE.Vector3(p.cx, p.cy, 0));

        // Check if this shape was created with line segments and has a closing segment
        // If so, don't create a closed spline (which would create a smooth curve at the closing point)
        let isClosedSpline = (shape.type === 'main' || shape.type === 'hole');

        // If the shape has segments (created with drawing tools), check if it's truly closed
        if (shape.segments && shape.segments.length > 0 && isClosedSpline) {
          // Check if the last segment connects back to the first point
          const firstPoint = shape.points[0];
          const lastPoint = shape.points[shape.points.length - 1];
          const TOLERANCE = 0.001;
          const distance = Math.sqrt(
            Math.pow(lastPoint.cx - firstPoint.cx, 2) +
            Math.pow(lastPoint.cy - firstPoint.cy, 2)
          );
          // If the last point is very close to the first, it's a properly closed shape
          // Otherwise, it might be an open path that shouldn't have a smooth closing curve
          isClosedSpline = distance < TOLERANCE;
        }

        const spline = new THREE.CatmullRomCurve3(splineControlPoints, isClosedSpline, 'catmullrom', 0.5);
        // Ensure splineDivisions is a positive integer, default to 30 if undefined or invalid
        const numDivisions = (shape.splineDivisions && shape.splineDivisions > 0) ? Math.floor(shape.splineDivisions) : 30;
        const splinePoints = spline.getPoints(numDivisions);

        if (splinePoints.length > 1) {
          // Use cyan for main, dark red for hole when it is the active shape
          const splineMaterialColor = isCurrentShape ? (shape.type === 'main' ? 0x00dddd : 0xcc0000) : 0x777777;
          const splineLineMaterial = new THREE.LineBasicMaterial({ color: splineMaterialColor, linewidth: 1.5 });
          const splineLineGeometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
          const splineLine = new THREE.Line(splineLineGeometry, splineLineMaterial);
          this.splineVisualizationLines.push(splineLine);
          this.scene.add(splineLine);
        }
      }
    });

    // console.log(`Total points created: ${this.pointMeshes.length}, Raycasting array size: ${this.scenePointMeshesForRaycasting.length}`);

    // Call highlightSelectedPointMesh to apply hover/selection if any
    this.highlightSelectedPointMesh(this.currentPointIndex);

    this.renderScene();
  }

  private highlightSelectedPointMesh(_: number): void {
    // Simple rule-based coloring applied every time something changes
    // 1) If point is hovered – yellow
    // 2) Else if point is in multi-selection – orange
    // 3) Else if point is the primary selected point – green
    // 4) Else if its shape is the currently active one – blue (main) or red (hole)
    // 5) Otherwise – gray (inactive shape)

    this.pointMeshes.forEach(mesh => {
      const shapeIdx = mesh.userData.shapeIndex as number;
      const pointIdx = mesh.userData.pointIndex as number;
      const shapeObj = this.shapes[shapeIdx];
      if (!shapeObj) return;

      let colour: number;

      // Hover always has highest priority
      if (mesh === this.hoveredPointMesh) {
        colour = 0xffff00; // yellow
      }
      // Multi-selected points (but not the primary selection)
      else if (shapeIdx === this.currentShapeIndex &&
        this.selectedPoints.includes(pointIdx) &&
        pointIdx !== this.currentPointIndex) {
        colour = 0xff8800; // orange for multi-selected points
      }
      // Primary selected point
      else if (mesh === this.selectedPointMesh) {
        colour = 0x00ff00; // green
      }
      // Base colour if point belongs to the currently selected shape
      else if (shapeIdx === this.currentShapeIndex) {
        colour = shapeObj.type === 'main' ? 0x0000ff : 0xff0000; // blue or red
      }
      // Otherwise dim grey
      else {
        colour = 0x666666;
      }

      (mesh.material as THREE.MeshBasicMaterial).color.setHex(colour);
    });

    this.renderScene();
  }


  apply() {
    var ij = Utils.cellname2indices(this.cellref);
    var ij2 = [ij[1], ij[0]]; // This is [col, row] for the main cell (e.g., splinePath)
    // let dataToApply = [].concat(...this.shapes.map(s => s.points.map(p => [p.cx, p.cy]))); // Original dataToApply, mostly for the dialog's own table if used differently

    const currentShape = this.shapes[this.currentShapeIndex];
    let splineDivisionsValue = 30; // Default

    if (currentShape) {
      splineDivisionsValue = currentShape.splineDivisions !== undefined ? currentShape.splineDivisions : 30;
    }

    // Use the same logic as updateData() to include all shapes (main + holes) separated by #
    // This ensures the extrusion engine gets the correct format: main_shape#hole1#hole2
    const splinePathValue = this.shapes.map(shape =>
      shape.points.map(p => `(${p.cx},${p.cy})`).join(';')
    ).join('#');

    // Prepare the main value string that represents all shapes with holes
    // This will go into the cell that was clicked to open the dialog.
    this.value1 = splinePathValue;

    // If copytable is true, the original complex logic for value1 is preserved.
    // Otherwise, value1 is simply the splinePathValue.
    if (this.copytable && currentShape && currentShape.points.length > 0) {
      let tempValue = '="';
      const excelIndices = Utils.cellname2indices(this.cellref); // Use original cellref for formula generation
      currentShape.points.forEach((p, i) => {
        const namex = Utils.indices2cellaname(excelIndices[0], excelIndices[1] + i);
        const namey = Utils.indices2cellaname(excelIndices[0] + 1, excelIndices[1] + i);
        tempValue += `(""${namex}"",""${namey}"");`;
      });
      if (tempValue.endsWith(';')) {
        tempValue = tempValue.slice(0, -1);
      }
      tempValue += '"';
      this.value1 = tempValue;
      // For copytable, td should still be the point array for the Handsontable in the dialog if it were used for that
      // However, the primary output mechanism for parameters is via specific named properties in the close() object.
    }

    this.dialogRef.close({
      val: this.value1, // This is the splinePath string or formula for copytable
      splinePath: splinePathValue, // Explicit spline path string
      splineDivisions: splineDivisionsValue, // Explicit spline divisions
      cellref: ij2, // target cell for `val` in [col, row] format
      copytable: this.copytable,
      // td: dataToApply // This was for the internal table, less relevant for parameter output
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // zoom = 1; // Replaced by zoomLevel for Three.js camera
  minZoom = 10; // Adjust these for orthographic camera sensitivity
  maxZoom = 200; // Higher value means more "zoomed in"

  zoomIn() {
    this.zoomLevel = Math.min(this.maxZoom, this.zoomLevel * 1.2);
    this.updateCameraProjection();
  }
  zoomOut() {
    this.zoomLevel = Math.max(this.minZoom, this.zoomLevel / 1.2);
    this.updateCameraProjection();
  }

  addPoint() {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (currentShape) {
      let newPoint: ShapePoint = { cx: 0, cy: 0 }; // Default to origin or center of view
      const points = currentShape.points;
      if (points.length > 0) {
        const lastPoint = points[points.length - 1];
        // Add point relative to the last point (data coordinates)
        newPoint = { cx: lastPoint.cx + (this.gridsize / 2), cy: lastPoint.cy + (this.gridsize / 2) };
      } else {
        // Add point at the center of the current view
        newPoint = { cx: this.panOffset3D.x, cy: this.panOffset3D.y };
      }

      // Check if the new point is too close to any existing point
      const TOLERANCE = 0.001;
      const isDuplicate = points.some(p => {
        const distance = Math.sqrt(
          Math.pow(p.cx - newPoint.cx, 2) +
          Math.pow(p.cy - newPoint.cy, 2)
        );
        return distance < TOLERANCE;
      });

      if (isDuplicate) {
        alert('Cannot add point at this location - too close to an existing point.');
        return;
      }

      points.push(newPoint);
      this.currentPointIndex = points.length - 1;
      this.updateData(); // Will update 3D objects and Handsontable
      if (this.hot) this.hot.selectCell(this.currentPointIndex, 0);
      // Make the newly added point the selected one.
      // Find the corresponding mesh. This happens after updateAndRenderThreeObjects.
      // For immediate effect, we need to ensure selectedPointMesh is set correctly.
      // This will be handled by the highlight called via updateData -> updateAndRender -> highlight.
      // To ensure this.selectedPointMesh is also correctly set, we can do it after updateData if needed.
      // However, selection primarily comes from interaction (click) or table selection.
      // For addPoint, selecting in table and updating currentPointIndex should be enough to trigger correct highlight.
      this.selectedPointMesh = this.pointMeshes.find(m => m.userData.shapeIndex === this.currentShapeIndex && m.userData.pointIndex === this.currentPointIndex) || null;

      this.highlightSelectedPointMesh(this.currentPointIndex);
    }
  }
  removePoint() {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape) return;

    // Handle multiple point removal
    if (this.selectedPoints.length > 0) {
      console.log('Removing multiple points:', this.selectedPoints);

      // Sort indices in descending order to remove from end first
      const sortedIndices = [...this.selectedPoints].sort((a, b) => b - a);

      sortedIndices.forEach(index => {
        if (index >= 0 && index < currentShape.points.length) {
          currentShape.points.splice(index, 1);
        }
      });

      // Clear multiple selection
      this.selectedPoints = [];
      this.currentPointIndex = -1;
      this.selectedPointMesh = null;
      this.highlightSelectedPointMesh(-1);

      console.log('Removed multiple points, remaining points:', currentShape.points.length);

      this.updateData();
      if (this.hot) {
        this.hot.deselectCell();
      }
      return;
    }

    // Handle single point removal (existing logic)
    if (currentShape && this.currentPointIndex > -1 && currentShape.points.length > 0) {
      currentShape.points.splice(this.currentPointIndex, 1);
      // If the removed point was selected, try to select the previous one or none
      this.currentPointIndex = Math.min(this.currentPointIndex, currentShape.points.length - 1);
      if (currentShape.points.length === 0) this.currentPointIndex = -1; // no points left

      this.selectedPointMesh = null; // Clear selection if point is removed or index becomes invalid
      if (this.currentPointIndex > -1) {
        this.selectedPointMesh = this.pointMeshes.find(m => m.userData.shapeIndex === this.currentShapeIndex && m.userData.pointIndex === this.currentPointIndex) || null;
      }

      this.updateData();
      if (this.hot && currentShape.points.length > 0 && this.currentPointIndex > -1) {
        this.hot.selectCell(this.currentPointIndex, 0);
      } else if (this.hot) {
        this.hot.deselectCell();
      }
      this.highlightSelectedPointMesh(this.currentPointIndex);
    } else if (currentShape && currentShape.points.length > 0) { // If no point was selected (currentPointIndex === -1), remove the last one
      currentShape.points.pop();
      this.currentPointIndex = currentShape.points.length - 1;
      if (currentShape.points.length === 0) this.currentPointIndex = -1;

      this.selectedPointMesh = null;
      if (this.currentPointIndex > -1) {
        this.selectedPointMesh = this.pointMeshes.find(m => m.userData.shapeIndex === this.currentShapeIndex && m.userData.pointIndex === this.currentPointIndex) || null;
      }
      this.updateData();
      if (this.hot && currentShape.points.length > 0 && this.currentPointIndex > -1) {
        this.hot.selectCell(this.currentPointIndex, 0);
      } else if (this.hot) {
        this.hot.deselectCell();
      }
      this.highlightSelectedPointMesh(this.currentPointIndex);
    }
  }

  removeDuplicatePoints(): void {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape || currentShape.points.length < 2) return;

    const TOLERANCE = this.snap ? this.gridsize * 0.1 : 0.001; // Adaptive tolerance based on snapping
    const cleanedPoints: ShapePoint[] = [currentShape.points[0]]; // Always keep the first point

    let removedCount = 0;
    let adjustedCurrentIndex = this.currentPointIndex;

    for (let i = 1; i < currentShape.points.length; i++) {
      const current = currentShape.points[i];
      const previous = cleanedPoints[cleanedPoints.length - 1];

      const distance = Math.sqrt(
        Math.pow(current.cx - previous.cx, 2) +
        Math.pow(current.cy - previous.cy, 2)
      );

      if (distance >= TOLERANCE) {
        cleanedPoints.push(current);
      } else {
        removedCount++;
        // Adjust current point index if we're removing a point before it
        if (i <= this.currentPointIndex) {
          adjustedCurrentIndex--;
        }
      }
    }

    // For closed shapes, also check if the last and first points are too close
    if ((currentShape.type === 'main' || currentShape.type === 'hole') && cleanedPoints.length > 2) {
      const first = cleanedPoints[0];
      const last = cleanedPoints[cleanedPoints.length - 1];
      const distance = Math.sqrt(
        Math.pow(last.cx - first.cx, 2) +
        Math.pow(last.cy - first.cy, 2)
      );

      if (distance < TOLERANCE) {
        cleanedPoints.pop(); // Remove the last point if it's too close to the first
        removedCount++;
        if (cleanedPoints.length - 1 < adjustedCurrentIndex) {
          adjustedCurrentIndex = cleanedPoints.length - 1;
        }
      }
    }

    if (removedCount > 0) {
      console.log(`Removed ${removedCount} duplicate points`);
      currentShape.points = cleanedPoints;
      this.currentPointIndex = Math.max(0, Math.min(adjustedCurrentIndex, cleanedPoints.length - 1));
      this.updateData();
    }
  }

  makeRoundCorner(): void {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape || this.currentPointIndex === -1 || currentShape.points.length < 3) {
      alert('Please select a point on a shape with at least 3 points.');
      return;
    }

    // First, clean up any duplicate adjacent points
    this.removeDuplicatePoints();

    // Re-check after cleanup
    if (currentShape.points.length < 3) {
      alert('After removing duplicates, the shape has fewer than 3 points.');
      return;
    }

    // Adjust currentPointIndex if it's out of bounds after cleanup
    if (this.currentPointIndex >= currentShape.points.length) {
      this.currentPointIndex = currentShape.points.length - 1;
    }

    const points = currentShape.points;
    const pIndex = this.currentPointIndex;

    // Determine points P0, P1 (corner), P2
    // P1 is the selected corner point: points[pIndex]
    // P0 is the point before P1
    // P2 is the point after P1

    const p1 = points[pIndex];
    let p0: ShapePoint, p2: ShapePoint;

    if (currentShape.type === 'main' || currentShape.type === 'hole') { // Closed shape, handle wrapping
      p0 = points[(pIndex - 1 + points.length) % points.length];
      p2 = points[(pIndex + 1) % points.length];
    } else { // Open shape (if you ever support this)
      if (pIndex === 0 || pIndex === points.length - 1) {
        alert('Cannot round the first or last point of an open shape.');
        return;
      }
      p0 = points[pIndex - 1];
      p2 = points[pIndex + 1];
    }

    // Convert to THREE.Vector2 for easier calculations
    const v0 = new THREE.Vector2(p0.cx, p0.cy);
    const v1 = new THREE.Vector2(p1.cx, p1.cy);
    const v2 = new THREE.Vector2(p2.cx, p2.cy);

    // Vectors from corner point P1 to P0 and P1 to P2
    const v10 = new THREE.Vector2().subVectors(v0, v1);
    const v12 = new THREE.Vector2().subVectors(v2, v1);

    const len10 = v10.length();
    const len12 = v12.length();

    // Use tolerance-based checking
    const TOLERANCE = 0.001;
    if (len10 < TOLERANCE || len12 < TOLERANCE) {
      alert('Adjacent points are too close together. Try moving them further apart or removing duplicates.');
      return;
    }

    // Normalize these vectors
    v10.normalize();
    v12.normalize();

    // Angle between v10 and v12 (cosine of angle)
    let dot = v10.dot(v12);
    // Ensure dot is within valid range for acos
    if (dot > 1.0) dot = 1.0;
    if (dot < -1.0) dot = -1.0;

    const angle = Math.acos(dot); // Angle at the vertex P1

    if (Math.abs(angle) < 0.0001 || Math.abs(angle - Math.PI) < 0.0001) {
      alert('Cannot round a straight line or a near-straight line.');
      return;
    }

    let effectiveRadius = this.cornerRadius;
    let tangentDist = effectiveRadius / Math.tan(angle / 2);

    // Clamp tangentDist if radius is too large for the segments
    if (tangentDist > len10 || tangentDist > len12) {
      tangentDist = Math.min(len10, len12) * 0.99; // Use 99% to avoid issues
      effectiveRadius = tangentDist * Math.tan(angle / 2); // This is the actual radius that will be used
      alert(`Radius was too large for the segments. Clamped to ${effectiveRadius.toFixed(2)}.`);
    }


    if (tangentDist <= 0) { // Should also catch effectiveRadius <= 0 implicitly
      alert('Radius is too small or results in invalid geometry.');
      return;
    }

    // Tangent points T0 on segment P1-P0, and T2 on segment P1-P2
    const t0 = new THREE.Vector2().addVectors(v1, v10.clone().multiplyScalar(tangentDist));
    const t2 = new THREE.Vector2().addVectors(v1, v12.clone().multiplyScalar(tangentDist));

    // Calculate the center of the arc
    // The center lies on the angle bisector.
    // Vector bisector direction
    const bisectorDir = new THREE.Vector2().addVectors(v10, v12).normalize();
    // Distance from P1 to arc center
    const distToCenter = effectiveRadius / Math.sin(angle / 2);
    const arcCenter = new THREE.Vector2().addVectors(v1, bisectorDir.clone().multiplyScalar(distToCenter));

    // Handle concavity: The bisectorDir might point "outside" or "inside" the polygon.
    // We need to ensure the arc curves correctly.
    // A simple check: the cross product (v10.x * v12.y - v10.y * v12.x) can tell us the turn direction.
    const crossProduct = v10.x * v12.y - v10.y * v12.x;
    if (crossProduct < 0) { // Clockwise turn (e.g., for a standard CCW polygon, this is a convex corner)
      // If the calculated center is further away from P1 than T0 or T2 along the bisector, it might be on the wrong side.
      // This check is simplified. A more robust check involves ensuring the arc is on the correct side of the segments.
      // For now, let's assume the basic bisector logic is okay, but we might need to flip the arc generation direction.
    } else { // Counter-clockwise turn (concave corner for a CCW polygon)
      // The center should be on the "other" side of the bisector.
      // In this case, the arcCenter calculation using distToCenter should correctly place it.
      // The main concern is the start and end angles for the arc.
    }


    // Generate points for the arc
    const arcPoints: ShapePoint[] = [];
    const numSegments = 10; // Number of segments for the arc, adjust as needed

    // Vectors from arcCenter to tangent points
    const vecCenterToT0 = new THREE.Vector2().subVectors(t0, arcCenter);
    const vecCenterToT2 = new THREE.Vector2().subVectors(t2, arcCenter);

    // Angles for T0 and T2 relative to arcCenter
    const loopStartAngle = vecCenterToT0.angle(); // Angle of T0 relative to arcCenter
    const trueArcSweepMagnitude = Math.PI - angle; // `angle` is the angle at vertex P1. Arc sweep is PI - this.

    let loopSweepAngle;
    // crossProduct is v10.x * v12.y - v10.y * v12.x, where v10=P0-P1, v12=P2-P1
    // If crossProduct < 0: P0P1 to P2P1 is CW turn. Convex corner for CCW polygon. Fillet sweeps CCW.
    // If crossProduct > 0: P0P1 to P2P1 is CCW turn. Concave corner for CCW polygon. Fillet sweeps CW.
    if (crossProduct < 0) {
      loopSweepAngle = trueArcSweepMagnitude; // Convex corner, sweep CCW
    } else if (crossProduct > 0) {
      loopSweepAngle = -trueArcSweepMagnitude; // Concave corner, sweep CW
    } else {
      // Straight line, should have been caught earlier. Defaulting to CCW sweep if somehow reached.
      // This case (crossProduct === 0 but angle is not PI or 0) should ideally not happen
      // due to earlier checks for colinearity and zero-length segments.
      loopSweepAngle = trueArcSweepMagnitude;
      console.warn("Cross product is zero for non-colinear points, check segment lengths or point coincidence. Defaulting sweep.");
    }

    // If angle is very close to PI (straight line), trueArcSweepMagnitude will be near 0.
    // This case is already handled by an earlier alert.
    // if (Math.abs(loopSweepAngle) < 1e-4) {
    //   console.warn("Calculated arc sweep is very small. Result might be just two points.");
    // }


    for (let i = 0; i <= numSegments; i++) {
      const t = i / numSegments;
      const currentAngle = loopStartAngle + t * loopSweepAngle;
      const x = arcCenter.x + effectiveRadius * Math.cos(currentAngle);
      const y = arcCenter.y + effectiveRadius * Math.sin(currentAngle);
      arcPoints.push({ cx: x, cy: y });
    }

    // Ensure t0 and t2 are effectively the start and end of the arcPoints if precision issues exist
    if (arcPoints.length > 0) {
      arcPoints[0] = { cx: t0.x, cy: t0.y };
      arcPoints[arcPoints.length - 1] = { cx: t2.x, cy: t2.y };
    } else { // Should not happen if numSegments > 0
      arcPoints.push({ cx: t0.x, cy: t0.y });
      arcPoints.push({ cx: t2.x, cy: t2.y });
    }


    // Replace the original point P1 with the new arcPoints
    // The order matters. If P0-P1-P2 was the sequence,
    // the new sequence will be P0 (implicitly) - arcPoints - P2 (implicitly).
    // We replace points[pIndex] with the first arc point, then insert the rest.
    if (arcPoints.length > 0) {
      points.splice(pIndex, 1, ...arcPoints);
    }


    // Adjust currentPointIndex if needed (e.g., deselect or select one of the new points)
    this.currentPointIndex = -1; // Deselect the point after operation
    this.selectedPointMesh = null;
    this.hoveredPointMesh = null;
    if (this.hot) this.hot.deselectCell();
    this.highlightSelectedPointMesh(-1);

    this.updateData(); // Refresh table and 3D view
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();

    // Get mouse position in normalized device coordinates (NDC)
    this.mouse.x = (event.offsetX / this.threeContainer.nativeElement.clientWidth) * 2 - 1;
    this.mouse.y = -(event.offsetY / this.threeContainer.nativeElement.clientHeight) * 2 + 1;

    // Create a vector for the mouse position in world space (on the Z=0 plane)
    const mouseWorldBeforeZoom = new THREE.Vector3(this.mouse.x, this.mouse.y, 0);
    mouseWorldBeforeZoom.unproject(this.camera); // Unproject from camera's current state
    // Since it's orthographic and we are on XY plane, Z is not critical here but unproject handles it.
    // For precise XY on plane:
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersectionBefore = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(this.plane, intersectionBefore);


    const zoomFactor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
    const oldZoomLevel = this.zoomLevel;
    this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel * zoomFactor));

    if (oldZoomLevel === this.zoomLevel) return; // No change in zoom

    // Update camera projection with new zoom FIRST
    this.updateCameraProjection(); // This updates camera.left, right, top, bottom based on new zoomLevel but old panOffset

    // Get mouse position in world space again with the new zoom, but old pan
    this.raycaster.setFromCamera(this.mouse, this.camera); // Raycaster needs updated camera
    const intersectionAfter = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(this.plane, intersectionAfter);

    // The difference is how much the world point under the mouse shifted due to zoom
    const dx = intersectionAfter.x - intersectionBefore.x;
    const dy = intersectionAfter.y - intersectionBefore.y;

    // Adjust panOffset to counteract this shift, keeping the point under the mouse stationary
    this.panOffset3D.x += dx;
    this.panOffset3D.y += dy;

    this.updateCameraProjection(); // Final update with new zoom AND new pan
    this.renderScene();
  }

  selectShape(index: number) {
    if (index === undefined || index === null || index < 0 || index >= this.shapes.length) {
      console.warn('Invalid index for selectShape:', index, 'Number of shapes:', this.shapes.length);
      if (this.shapes.length > 0) {
        this.currentShapeIndex = 0;
      } else {
        return;
      }
    } else {
      this.currentShapeIndex = index;
    }
    this.currentPointIndex = -1;
    this.selectedPointMesh = null; // Clear 3D point selection
    this.hoveredPointMesh = null; // Clear hover state when changing shapes
    this.updateData(); // This will update Handsontable and rebuild 3D objects for the new shape context
    if (this.hot) this.hot.deselectCell(); // Deselect in table
    this.highlightSelectedPointMesh(-1); // Clear visual highlight
  }

  addHole() {
    console.log('Adding new hole - switching to line tool');

    // Create empty hole shape
    const newHoleId = `hole-${Date.now()}-${this.shapes.filter(s => s.type === 'hole').length}`;

    this.shapes.push({
      type: 'hole',
      points: [],
      segments: [],
      id: newHoleId,
      splineDivisions: 30,
      shapeType: 'polygon'
    });

    // Switch to the new hole
    this.currentShapeIndex = this.shapes.length - 1;
    this.currentPointIndex = -1;
    this.selectedPointMesh = null;
    this.hoveredPointMesh = null;

    // Switch to line tool to start drawing
    this.setTool(DrawingTool.LINE);

    // Show helpful message
    console.log('Switched to line tool. Start drawing the hole by clicking points. Double-click to close the shape.');

    this.updateData();
    if (this.hot) this.hot.deselectCell();
    this.highlightSelectedPointMesh(-1);
  }

  removeCurrentShape() {
    if (this.shapes[this.currentShapeIndex]?.type === 'hole') {
      this.shapes.splice(this.currentShapeIndex, 1);
      if (this.currentShapeIndex >= this.shapes.length) {
        this.currentShapeIndex = this.shapes.length - 1;
      }
      if (this.currentShapeIndex < 0 && this.shapes.length > 0) {
        this.currentShapeIndex = 0;
      } else if (this.shapes.length === 0) {
        this.shapes.push({
          type: 'main',
          points: [],
          segments: [],
          id: 'main-fallback-empty',
          splineDivisions: 30,
          shapeType: 'polygon'
        });
        this.currentShapeIndex = 0;
      }
      this.currentPointIndex = -1;
      this.selectedPointMesh = null;
      this.hoveredPointMesh = null;
      this.updateData();
      if (this.hot) this.hot.deselectCell();
      this.highlightSelectedPointMesh(-1);
    } else {
      if (this.shapes[this.currentShapeIndex]) {
        this.shapes[this.currentShapeIndex].points = [];
        this.currentPointIndex = -1;
        this.selectedPointMesh = null;
        this.hoveredPointMesh = null;
        this.updateData();
        if (this.hot) this.hot.deselectCell();
        this.highlightSelectedPointMesh(-1);
      }
    }
  }

  getCurrentShapeName(): string {
    if (!this.shapes[this.currentShapeIndex]) return 'No Shape';
    const shape = this.shapes[this.currentShapeIndex];
    if (shape.type === 'main') return 'Main Shape';
    // Find the true index of this hole among other holes for naming like "Hole 1", "Hole 2"
    let holeCounter = 0;
    for (let i = 0; i <= this.currentShapeIndex; i++) {
      if (this.shapes[i].type === 'hole') holeCounter++;
      if (i === this.currentShapeIndex) break;
    }
    return `Hole ${holeCounter}`;
  }

  getShapeTooltip(shape: ShapeObject): string {
    if (shape.type === 'main') {
      return 'Main Outline';
    }
    // Calculate hole number for tooltip
    let holeCounter = 0;
    let found = false;
    for (let i = 0; i < this.shapes.length; i++) {
      if (this.shapes[i].type === 'hole') {
        holeCounter++;
      }
      if (this.shapes[i].id === shape.id) {
        found = true;
        break;
      }
    }
    return found ? `Hole ${holeCounter}` : 'Hole';
  }

  getShapeButtonText(shape: ShapeObject): string {
    if (shape.type === 'main') {
      return 'Main';
    }
    // Calculate hole number for button text
    let holeCounter = 0;
    let found = false;
    for (let i = 0; i < this.shapes.length; i++) {
      if (this.shapes[i].type === 'hole') {
        holeCounter++;
      }
      if (this.shapes[i].id === shape.id) {
        found = true;
        break;
      }
    }
    return found ? `H${holeCounter}` : 'H';
  }

  // ===== DRAWING TOOLS METHODS =====

  setTool(tool: DrawingTool): void {
    console.log('Switching to tool:', tool);
    this.currentTool = tool;
    this.isDrawing = false;
    this.drawingStartPoint = null;
    this.lastClickTime = 0;

    // Clean up all drawing visual elements
    this.clearPreviewLine();
    this.clearPreviewCircle();
    this.clearStartPointVisual();
    this.clearSnapIndicator();

    if (this.tempDrawingGeometry) {
      this.scene.remove(this.tempDrawingGeometry);
      this.tempDrawingGeometry = null;
    }

    // Clear any selection when switching tools
    this.clearSelection();
  }

  addLineSegment(startPoint: ShapePoint, endPoint: ShapePoint): void {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape.segments) {
      currentShape.segments = [];
    }

    const segment: ShapeSegment = {
      type: 'line',
      points: [startPoint, endPoint],
      id: `line-${Date.now()}-${Math.random()}`
    };

    currentShape.segments.push(segment);
    this.updatePointsFromSegments();
    this.updateAndRenderThreeObjects();
    this.updateData();
  }

  addSplineSegment(controlPoints: ShapePoint[]): void {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape.segments) {
      currentShape.segments = [];
    }

    const segment: ShapeSegment = {
      type: 'spline',
      points: controlPoints,
      id: `spline-${Date.now()}-${Math.random()}`,
      splineDivisions: currentShape.splineDivisions || 30
    };

    currentShape.segments.push(segment);
    this.updatePointsFromSegments();
    this.updateAndRenderThreeObjects();
    this.updateData();
  }

  createCircleHole(center: ShapePoint, radius: number): void {
    const circleId = `hole-${Date.now()}`;
    const circleShape: ShapeObject = {
      type: 'hole',
      points: [],
      segments: [],
      id: circleId,
      shapeType: 'circle',
      center: center,
      radius: radius,
      splineDivisions: 32
    };

    // Generate circle points for compatibility
    this.generateCirclePoints(circleShape);

    this.shapes.push(circleShape);
    this.currentShapeIndex = this.shapes.length - 1;
    this.updateAndRenderThreeObjects();
    this.updateData();
  }

  generateCirclePoints(shape: ShapeObject): void {
    if (shape.shapeType === 'circle' && shape.center && shape.radius) {
      shape.points = [];
      const divisions = shape.splineDivisions || 32;

      for (let i = 0; i < divisions; i++) {
        const angle = (i / divisions) * Math.PI * 2;
        const x = shape.center.cx + Math.cos(angle) * shape.radius;
        const y = shape.center.cy + Math.sin(angle) * shape.radius;
        shape.points.push({ cx: x, cy: y });
      }
    }
  }

  updatePointsFromSegments(): void {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape.segments || currentShape.segments.length === 0) {
      return;
    }

    currentShape.points = [];

    // For line segments, we need to build a connected path without duplicates
    if (currentShape.segments.every(seg => seg.type === 'line')) {
      // All segments are lines - build a connected path
      if (currentShape.segments.length > 0) {
        // Add the first point of the first segment
        const firstSegment = currentShape.segments[0];
        if (firstSegment.points.length > 0) {
          currentShape.points.push(firstSegment.points[0]);
        }

        // For each segment, add only the end point (since start point should connect to previous end point)
        currentShape.segments.forEach((segment, index) => {
          if (segment.points.length > 1) {
            // Check if this is the last segment and it connects back to the first point
            if (index === currentShape.segments.length - 1) {
              const lastPoint = segment.points[1];
              const firstPoint = currentShape.points[0];
              // Only add if it's not the same as the first point (closing segment)
              const TOLERANCE = 0.001;
              const distance = Math.sqrt(
                Math.pow(lastPoint.cx - firstPoint.cx, 2) +
                Math.pow(lastPoint.cy - firstPoint.cy, 2)
              );
              if (distance > TOLERANCE) {
                currentShape.points.push(segment.points[1]);
              }
            } else {
              currentShape.points.push(segment.points[1]);
            }
          }
        });
      }
    } else {
      // Mixed segments or non-line segments - use original logic
      currentShape.segments.forEach(segment => {
        if (segment.type === 'line') {
          currentShape.points.push(...segment.points);
        } else if (segment.type === 'spline') {
          // Generate spline points
          const splinePoints = this.generateSplinePoints(segment.points, segment.splineDivisions || 30);
          currentShape.points.push(...splinePoints);
        }
      });
    }
  }

  generateSplinePoints(controlPoints: ShapePoint[], divisions: number): ShapePoint[] {
    if (controlPoints.length < 2) return controlPoints;

    const curve = new THREE.CatmullRomCurve3(
      controlPoints.map(p => new THREE.Vector3(p.cx, p.cy, 0))
    );

    const curvePoints = curve.getPoints(divisions);
    return curvePoints.map(p => ({ cx: p.x, cy: p.y }));
  }

  // Handle mouse interactions for drawing tools
  handleDrawingMouseDown(worldPos: THREE.Vector3): boolean {
    const point: ShapePoint = {
      cx: this.snap ? Math.round(worldPos.x / this.gridsize) * this.gridsize : worldPos.x,
      cy: this.snap ? Math.round(worldPos.y / this.gridsize) * this.gridsize : worldPos.y
    };

    switch (this.currentTool) {
      case DrawingTool.LINE:
        const currentTime = Date.now();
        const isDoubleClick = this.isDrawing && (currentTime - this.lastClickTime) < this.doubleClickThreshold;

        if (isDoubleClick) {
          // Double-click: close the shape
          console.log('Double-click detected, closing shape');
          this.closeCurrentShape();
          this.lastClickTime = 0; // Reset to prevent triple-click issues
          return true;
        }

        this.lastClickTime = currentTime;

        if (!this.isDrawing) {
          // First click: start drawing
          console.log('Starting line drawing at:', point);
          this.isDrawing = true;
          this.drawingStartPoint = point;
          this.createPreviewLine(point, point);
          this.addStartPointVisual(point); // Add visual indicator for start point
        } else { // Subsequent clicks
          const currentDrawingShape = this.shapes[this.currentShapeIndex];
          let isClickOnActualStartPoint = false;

          // Check if the current click 'point' is on the *actual* start point of the current shape being drawn.
          // This requires at least one segment to define a start point.
          if (currentDrawingShape.segments && currentDrawingShape.segments.length > 0) {
            const firstPointOfDrawnShape = currentDrawingShape.segments[0].points[0];
            // 'point' is the current snapped click position
            const distToStart = Math.sqrt(
              Math.pow(point.cx - firstPointOfDrawnShape.cx, 2) +
              Math.pow(point.cy - firstPointOfDrawnShape.cy, 2)
            );
            // Use a small tolerance for comparing floating point coordinates
            const CLICK_ON_START_TOLERANCE = 0.1;
            if (distToStart < CLICK_ON_START_TOLERANCE) {
              isClickOnActualStartPoint = true;
            }
          }

          if (isClickOnActualStartPoint) {
            // Single click on the start point.
            // As per "don't close shape before double click", this should NOT add the closing segment.
            // The double-click handler will take care of closing.
            // drawingStartPoint remains the previous point, and the preview line will update accordingly on mouse move.
            console.log('Single click on start point while drawing: not adding segment. Waiting for double-click to close.');
            // The preview line will be updated by mousemove to show the line from the last actual point to the current mouse position (over the start point).
          } else {
            // Subsequent clicks NOT on the start point: add line segment and continue
            if (this.drawingStartPoint) {
              console.log('Adding line segment from', this.drawingStartPoint, 'to', point);
              this.addLineSegment(this.drawingStartPoint, point);
              this.drawingStartPoint = point; // Continue from this point
              // Update preview line to be a dot at the new drawingStartPoint.
              // The main preview line update is handled by mousemove.
              this.updatePreviewLine(point, point);
            }
          }
        }
        return true;

      case DrawingTool.CIRCLE:
        if (!this.isDrawing) {
          this.isDrawing = true;
          this.drawingStartPoint = point;
          console.log('Starting circle drawing at center:', point);
          // Add visual indicator for circle center
          this.addStartPointVisual(point);
          // Create initial preview circle with radius 0
          this.createPreviewCircle(point, 0);
        } else {
          if (this.drawingStartPoint) {
            const radius = Math.sqrt(
              Math.pow(point.cx - this.drawingStartPoint.cx, 2) +
              Math.pow(point.cy - this.drawingStartPoint.cy, 2)
            );
            console.log('Creating circle with center:', this.drawingStartPoint, 'radius:', radius);
            this.createCircleHole(this.drawingStartPoint, radius);
            // Clean up preview elements
            this.clearPreviewCircle();
            this.clearStartPointVisual();
            this.clearSnapIndicator();
          }
          this.isDrawing = false;
          this.drawingStartPoint = null;
        }
        return true;


    }

    return false;
  }



  createPreviewLine(startPoint: ShapePoint, endPoint: ShapePoint): void {
    if (this.previewLine) {
      this.scene.remove(this.previewLine);
      if (this.previewLine.geometry) this.previewLine.geometry.dispose();
      if (this.previewLine.material) (this.previewLine.material as THREE.Material).dispose();
    }

    const geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(startPoint.cx, startPoint.cy, 0.1), // Slightly above other lines
      new THREE.Vector3(endPoint.cx, endPoint.cy, 0.1)
    ]);

    const material = new THREE.LineBasicMaterial({
      color: 0x00ff00, // Bright green for preview
      opacity: 0.8,
      transparent: true,
      linewidth: 3
    });

    this.previewLine = new THREE.Line(geometry, material);
    this.scene.add(this.previewLine);
    console.log('Preview line created:', startPoint, endPoint);
  }

  updatePreviewLine(startPoint: ShapePoint, endPoint: ShapePoint): void {
    if (this.previewLine) {
      const positions = new Float32Array([
        startPoint.cx, startPoint.cy, 0.1,
        endPoint.cx, endPoint.cy, 0.1
      ]);
      this.previewLine.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      this.previewLine.geometry.attributes.position.needsUpdate = true;
      this.renderScene(); // Force immediate render
    }
  }

  closeCurrentShape(): void {
    console.log('Closing current shape, isDrawing:', this.isDrawing);

    if (this.isDrawing && this.drawingStartPoint && this.shapes[this.currentShapeIndex].segments) {
      // Add final line segment to close the shape if we have segments
      const segments = this.shapes[this.currentShapeIndex].segments;
      if (segments && segments.length > 0) {
        const firstSegment = segments[0];
        const firstPoint = firstSegment.points[0];

        if (this.drawingStartPoint.cx !== firstPoint.cx || this.drawingStartPoint.cy !== firstPoint.cy) {
          console.log('Adding closing line segment');
          this.addLineSegment(this.drawingStartPoint, firstPoint);
        }
      }
    }

    // Clean up drawing state
    this.isDrawing = false;
    this.drawingStartPoint = null;

    // Clean up visual elements
    this.clearPreviewLine();
    this.clearStartPointVisual();
    this.clearSnapIndicator();

    this.updateAndRenderThreeObjects();

    // Auto-switch to select tool after closing shape
    console.log('Auto-switching to select tool');
    this.setTool(DrawingTool.SELECT);
  }

  clearPreviewLine(): void {
    if (this.previewLine) {
      this.scene.remove(this.previewLine);
      if (this.previewLine.geometry) this.previewLine.geometry.dispose();
      if (this.previewLine.material) (this.previewLine.material as THREE.Material).dispose();
      this.previewLine = null;
    }
  }

  addStartPointVisual(point: ShapePoint): void {
    this.clearStartPointVisual();

    // Create a proper point mesh that can be hovered (same as regular points)
    const pointGeometry = new THREE.BoxGeometry(8, 8, 8);
    const currentShape = this.shapes[this.currentShapeIndex];
    const initialColor = currentShape?.type === 'main' ? 0x0000ff : 0xff0000; // Blue for main, Red for hole
    const pointMaterial = new THREE.MeshBasicMaterial({ color: initialColor });

    this.startPointMesh = new THREE.Mesh(pointGeometry, pointMaterial);
    this.startPointMesh.position.set(point.cx, point.cy, 0);

    // Set userData so it can be identified as a point for hover effects
    this.startPointMesh.userData = {
      shapeIndex: this.currentShapeIndex,
      pointIndex: -1, // Special index for start point
      type: 'point',
      shapeType: currentShape?.type || 'main',
      isStartPoint: true // Flag to identify this as the drawing start point
    };

    this.scene.add(this.startPointMesh);

    // Add to raycasting array so it can be hovered
    this.scenePointMeshesForRaycasting.push(this.startPointMesh);

    // Also add to pointMeshes array for consistent management
    this.pointMeshes.push(this.startPointMesh);
  }

  clearStartPointVisual(): void {
    if (this.startPointMesh) {
      this.scene.remove(this.startPointMesh);

      // Remove from raycasting array
      const raycastIndex = this.scenePointMeshesForRaycasting.indexOf(this.startPointMesh);
      if (raycastIndex >= 0) {
        this.scenePointMeshesForRaycasting.splice(raycastIndex, 1);
      }

      // Remove from pointMeshes array
      const pointMeshIndex = this.pointMeshes.indexOf(this.startPointMesh);
      if (pointMeshIndex >= 0) {
        this.pointMeshes.splice(pointMeshIndex, 1);
      }

      // Clear hover state if this was the hovered mesh
      if (this.hoveredPointMesh === this.startPointMesh) {
        this.hoveredPointMesh = null;
      }

      // Clear selection state if this was the selected mesh
      if (this.selectedPointMesh === this.startPointMesh) {
        this.selectedPointMesh = null;
      }

      if (this.startPointMesh.geometry) this.startPointMesh.geometry.dispose();
      if (this.startPointMesh.material) (this.startPointMesh.material as THREE.Material).dispose();
      this.startPointMesh = null;
    }
  }

  addSnapIndicator(point: ShapePoint): void {
    this.clearSnapIndicator();

    const geometry = new THREE.RingGeometry(8, 12, 8);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff, // Cyan color for snap indicator
      opacity: 0.6,
      transparent: true,
      side: THREE.DoubleSide
    });

    this.snapIndicator = new THREE.Mesh(geometry, material);
    this.snapIndicator.position.set(point.cx, point.cy, 0.15);
    this.scene.add(this.snapIndicator);
  }

  clearSnapIndicator(): void {
    if (this.snapIndicator) {
      this.scene.remove(this.snapIndicator);
      if (this.snapIndicator.geometry) this.snapIndicator.geometry.dispose();
      if (this.snapIndicator.material) (this.snapIndicator.material as THREE.Material).dispose();
      this.snapIndicator = null;
    }
  }
  // ===== SELECTION METHODS =====

  setSelectionMode(mode: SelectionMode): void {
    this.selectionMode = mode;
    this.clearSelection();
  }

  addPointBetweenNearest(clickPos: THREE.Vector3): void {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape || currentShape.points.length < 2) {
      console.log('Need at least 2 points to insert between');
      return;
    }

    // Find the nearest edge (line segment between two consecutive points)
    let minDistance = Infinity;
    let bestIndex = 0;

    for (let i = 0; i < currentShape.points.length; i++) {
      const p1 = currentShape.points[i];
      const p2 = currentShape.points[(i + 1) % currentShape.points.length]; // Wrap around for closed shapes

      // Calculate distance from click position to line segment
      const lineStart = new THREE.Vector2(p1.cx, p1.cy);
      const lineEnd = new THREE.Vector2(p2.cx, p2.cy);
      const clickPoint = new THREE.Vector2(clickPos.x, clickPos.y);

      // Calculate perpendicular distance to line segment
      const lineVec = new THREE.Vector2().subVectors(lineEnd, lineStart);
      const lineLength = lineVec.length();

      if (lineLength === 0) continue; // Skip if points are identical

      lineVec.normalize();

      // Project click point onto the line
      const toClick = new THREE.Vector2().subVectors(clickPoint, lineStart);
      const projection = toClick.dot(lineVec);

      // Clamp projection to line segment
      const clampedProjection = Math.max(0, Math.min(lineLength, projection));

      // Find closest point on line segment
      const closestPoint = new THREE.Vector2().addVectors(
        lineStart,
        lineVec.clone().multiplyScalar(clampedProjection)
      );

      // Calculate distance from click to closest point on segment
      const distance = clickPoint.distanceTo(closestPoint);

      if (distance < minDistance) {
        minDistance = distance;
        bestIndex = i;
      }
    }

    // Insert new point at the midpoint of the nearest edge
    const p1 = currentShape.points[bestIndex];
    const p2 = currentShape.points[(bestIndex + 1) % currentShape.points.length];

    const newPoint: ShapePoint = {
      cx: (p1.cx + p2.cx) / 2,
      cy: (p1.cy + p2.cy) / 2
    };

    // Apply snapping if enabled
    if (this.snap) {
      newPoint.cx = Math.round(newPoint.cx / this.gridsize) * this.gridsize;
      newPoint.cy = Math.round(newPoint.cy / this.gridsize) * this.gridsize;
    }

    // Insert the new point after bestIndex
    currentShape.points.splice(bestIndex + 1, 0, newPoint);

    // Select the newly added point
    this.currentPointIndex = bestIndex + 1;
    this.selectedPointMesh = null; // Will be set after updateData creates the mesh

    // Update the data and 3D visualization
    this.updateData();

    // Select the new point in the table
    if (this.hot) {
      this.hot.selectCell(this.currentPointIndex, 0, this.currentPointIndex, 1, false, false);
    }

    // Find and select the newly created mesh
    setTimeout(() => {
      this.selectedPointMesh = this.pointMeshes.find(m =>
        m.userData.shapeIndex === this.currentShapeIndex &&
        m.userData.pointIndex === this.currentPointIndex
      ) || null;
      this.highlightSelectedPointMesh(this.currentPointIndex);
    }, 0);

    console.log(`Added new point at index ${this.currentPointIndex} between points ${bestIndex} and ${(bestIndex + 1) % currentShape.points.length}`);
  }

  clearSelection(): void {
    this.selectedPoints = [];
    this.selectedSegmentIndex = -1;
    this.selectedShapeForMove = -1;
    this.clearBoundingBox();
    this.clearGhostPoints();
    this.updateAndRenderThreeObjects();
  }

  selectEntireShape(shapeIndex: number): void {
    this.selectedShapeForMove = shapeIndex;
    this.selectedPoints = [];
    this.selectedSegmentIndex = -1;
    this.calculateBoundingBox();
    this.updateAndRenderThreeObjects();
  }

  selectSegment(segmentIndex: number): void {
    this.selectedSegmentIndex = segmentIndex;
    this.selectedShapeForMove = -1;
    this.selectedPoints = [];
    this.calculateBoundingBox();
    this.updateAndRenderThreeObjects();
  }

  selectMultiplePoints(pointIndices: number[]): void {
    this.selectedPoints = [...pointIndices];
    this.selectedShapeForMove = -1;
    this.selectedSegmentIndex = -1;
    this.calculateBoundingBox();
    this.updateAndRenderThreeObjects();
  }

  calculateBoundingBox(): void {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape) return;

    let points: ShapePoint[] = [];

    if (this.selectedShapeForMove === this.currentShapeIndex) {
      // Entire shape selected
      points = currentShape.points;
    } else if (this.selectedSegmentIndex >= 0 && currentShape.segments) {
      // Segment selected
      const segment = currentShape.segments[this.selectedSegmentIndex];
      if (segment) {
        points = segment.points;
      }
    } else if (this.selectedPoints.length > 0) {
      // Multiple points selected
      points = this.selectedPoints.map(index => currentShape.points[index]).filter(p => p);
    }

    if (points.length === 0) return;

    const min = { x: Infinity, y: Infinity };
    const max = { x: -Infinity, y: -Infinity };

    points.forEach(point => {
      min.x = Math.min(min.x, point.cx);
      min.y = Math.min(min.y, point.cy);
      max.x = Math.max(max.x, point.cx);
      max.y = Math.max(max.y, point.cy);
    });

    this.selectionBounds = new THREE.Box3(
      new THREE.Vector3(min.x, min.y, -1),
      new THREE.Vector3(max.x, max.y, 1)
    );

    this.updateBoundingBoxVisualization();
  }

  updateBoundingBoxVisualization(): void {
    this.clearBoundingBox();

    if (!this.selectionBounds) return;

    // Create a visual bounding box
    const geometry = new THREE.BoxGeometry(
      this.selectionBounds.max.x - this.selectionBounds.min.x,
      this.selectionBounds.max.y - this.selectionBounds.min.y,
      0.1
    );

    const material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      wireframe: true,
      transparent: true,
      opacity: 0.5
    });

    const boundingBoxMesh = new THREE.Mesh(geometry, material);
    boundingBoxMesh.position.set(
      (this.selectionBounds.min.x + this.selectionBounds.max.x) / 2,
      (this.selectionBounds.min.y + this.selectionBounds.max.y) / 2,
      0
    );

    this.scene.add(boundingBoxMesh);
    this.boundingBoxHelper = new THREE.BoxHelper(boundingBoxMesh, 0x00ff00);
    this.scene.add(this.boundingBoxHelper);
  }

  clearBoundingBox(): void {
    if (this.boundingBoxHelper) {
      this.scene.remove(this.boundingBoxHelper);
      this.boundingBoxHelper = null;
    }
    this.selectionBounds = null;
  }

  createGhostPoints(): void {
    this.clearGhostPoints();

    if (this.selectedPoints.length === 0) return;

    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape) return;

    this.selectedPoints.forEach(pointIndex => {
      const point = currentShape.points[pointIndex];
      if (point) {
        // Create a ghost point at the original position
        const ghostGeometry = new THREE.SphereGeometry(3, 8, 6);
        const ghostMaterial = new THREE.MeshBasicMaterial({
          color: 0x888888,
          opacity: 0.5,
          transparent: true,
          wireframe: true
        });

        const ghostMesh = new THREE.Mesh(ghostGeometry, ghostMaterial);
        ghostMesh.position.set(point.cx, point.cy, -0.1); // Slightly behind
        ghostMesh.userData = { type: 'ghost', originalPointIndex: pointIndex };

        this.scene.add(ghostMesh);
        this.moveGhostMeshes.push(ghostMesh);
      }
    });

    console.log(`Created ${this.moveGhostMeshes.length} ghost points`);
  }

  clearGhostPoints(): void {
    this.moveGhostMeshes.forEach(mesh => {
      this.scene.remove(mesh);
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) (mesh.material as THREE.Material).dispose();
    });
    this.moveGhostMeshes = [];
  }



  isPointInSelection(worldPos: THREE.Vector3): boolean {
    if (!this.selectionBounds) return false;

    return worldPos.x >= this.selectionBounds.min.x &&
      worldPos.x <= this.selectionBounds.max.x &&
      worldPos.y >= this.selectionBounds.min.y &&
      worldPos.y <= this.selectionBounds.max.y;
  }

  findSegmentContainingPoint(pointIndex: number): number {
    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape.segments) return -1;

    // This is a simplified approach - in a real implementation you'd need
    // to track which points belong to which segments more precisely
    let currentPointIndex = 0;

    for (let i = 0; i < currentShape.segments.length; i++) {
      const segment = currentShape.segments[i];
      const segmentPointCount = segment.type === 'spline' ?
        (segment.splineDivisions || 30) : segment.points.length;

      if (pointIndex >= currentPointIndex && pointIndex < currentPointIndex + segmentPointCount) {
        return i;
      }
      currentPointIndex += segmentPointCount;
    }

    return -1;
  }



  // ===== KEYBOARD SHORTCUTS =====

  onKeyDown(event: KeyboardEvent): void {
    // Only handle shortcuts when not typing in input fields
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        this.selectedPoints = [];
        this.selectedPointMesh = null;
        this.currentPointIndex = -1;
        this.clearSelection();
        this.setTool(DrawingTool.SELECT);
        this.highlightSelectedPointMesh(-1);
        if (this.hot) this.hot.deselectCell();
        event.preventDefault();
        break;
      case 's':
      case 'S':
        if (!event.ctrlKey && !event.metaKey) {
          this.setTool(DrawingTool.SELECT);
          event.preventDefault();
        }
        break;

      case 'l':
      case 'L':
        if (!event.ctrlKey && !event.metaKey) {
          this.setTool(DrawingTool.LINE);
          event.preventDefault();
        }
        break;
      case 'c':
      case 'C':
        if (!event.ctrlKey && !event.metaKey) {
          this.setTool(DrawingTool.CIRCLE);
          event.preventDefault();
        }
        break;
      case 'Delete':
      case 'Backspace':
        if (this.selectedPoints.length > 0 || this.currentPointIndex >= 0) {
          this.removePoint();
          event.preventDefault();
        }
        break;

    }
  }

  // ===== RECTANGLE SELECTION METHODS =====

  startRectangleSelection(worldPos: THREE.Vector3): void {
    this.isRectangleSelecting = true;
    this.rectangleSelectionStart = new THREE.Vector2(worldPos.x, worldPos.y);
    this.rectangleSelectionEnd = new THREE.Vector2(worldPos.x, worldPos.y);

    // Clear previous selection unless Ctrl/Cmd is held
    if (!event || (!(event as MouseEvent).ctrlKey && !(event as MouseEvent).metaKey)) {
      this.selectedPoints = [];
      this.selectedPointMesh = null;
      this.currentPointIndex = -1;
    }

    this.createSelectionRectangle();
  }

  updateSelectionRectangle(): void {
    if (!this.rectangleSelectionStart || !this.rectangleSelectionEnd) return;

    const minX = Math.min(this.rectangleSelectionStart.x, this.rectangleSelectionEnd.x);
    const maxX = Math.max(this.rectangleSelectionStart.x, this.rectangleSelectionEnd.x);
    const minY = Math.min(this.rectangleSelectionStart.y, this.rectangleSelectionEnd.y);
    const maxY = Math.max(this.rectangleSelectionStart.y, this.rectangleSelectionEnd.y);

    const width = maxX - minX;
    const height = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    if (this.selectionRectangleMesh) {
      // Update existing rectangle
      this.selectionRectangleMesh.scale.set(width, height, 1);
      this.selectionRectangleMesh.position.set(centerX, centerY, 0.2);
    }

    this.renderScene();
  }

  createSelectionRectangle(): void {
    this.clearSelectionRectangle();

    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      color: 0x0088ff,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide
    });

    this.selectionRectangleMesh = new THREE.Mesh(geometry, material);
    this.selectionRectangleMesh.position.z = 0.2; // Above other objects
    this.scene.add(this.selectionRectangleMesh);

    // Also create wireframe border
    const wireframeGeometry = new THREE.EdgesGeometry(geometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: 0x0088ff,
      linewidth: 2
    });
    const wireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
    wireframe.position.z = 0.21; // Slightly above the rectangle
    this.selectionRectangleMesh.add(wireframe);
  }

  clearSelectionRectangle(): void {
    if (this.selectionRectangleMesh) {
      this.scene.remove(this.selectionRectangleMesh);
      if (this.selectionRectangleMesh.geometry) this.selectionRectangleMesh.geometry.dispose();
      if (this.selectionRectangleMesh.material) (this.selectionRectangleMesh.material as THREE.Material).dispose();
      this.selectionRectangleMesh = null;
    }
  }

  finishRectangleSelection(): void {
    if (!this.rectangleSelectionStart || !this.rectangleSelectionEnd) {
      this.isRectangleSelecting = false;
      this.clearSelectionRectangle();
      return;
    }

    const minX = Math.min(this.rectangleSelectionStart.x, this.rectangleSelectionEnd.x);
    const maxX = Math.max(this.rectangleSelectionStart.x, this.rectangleSelectionEnd.x);
    const minY = Math.min(this.rectangleSelectionStart.y, this.rectangleSelectionEnd.y);
    const maxY = Math.max(this.rectangleSelectionStart.y, this.rectangleSelectionEnd.y);

    // Find all points within the selection rectangle
    const currentShape = this.shapes[this.currentShapeIndex];
    if (currentShape) {
      const newSelectedPoints: number[] = [];

      currentShape.points.forEach((point, index) => {
        if (point.cx >= minX && point.cx <= maxX &&
          point.cy >= minY && point.cy <= maxY) {
          newSelectedPoints.push(index);
        }
      });

      // Add to existing selection or replace based on Ctrl/Cmd key
      const event = window.event as MouseEvent;
      if (event && (event.ctrlKey || event.metaKey)) {
        // Add to existing selection
        newSelectedPoints.forEach(index => {
          if (!this.selectedPoints.includes(index)) {
            this.selectedPoints.push(index);
          }
        });
      } else {
        // Replace selection
        this.selectedPoints = newSelectedPoints;
      }

      console.log(`Rectangle selection: selected ${this.selectedPoints.length} points`);

      // Update visual highlights
      this.highlightSelectedPointMesh(this.currentPointIndex);

      // If points were selected and no primary point is set, or the primary isn't in the selection,
      // set the first selected point as the primary for potential immediate drag.
      if (this.selectedPoints.length > 0) {
        if (this.currentPointIndex === -1 || !this.selectedPoints.includes(this.currentPointIndex)) {
          this.currentPointIndex = this.selectedPoints[0];
        }
        this.selectedPointMesh = this.pointMeshes.find(m =>
          m.userData.shapeIndex === this.currentShapeIndex &&
          m.userData.pointIndex === this.currentPointIndex
        ) || null;

        if (this.hot && this.currentPointIndex !== -1) {
          this.hot.selectCell(this.currentPointIndex, 0, this.currentPointIndex, 1, false, false);
        }
      } else {
        // No points selected, clear primary selection too
        this.currentPointIndex = -1;
        this.selectedPointMesh = null;
        if (this.hot) this.hot.deselectCell();
      }

    }

    // Clean up
    this.isRectangleSelecting = false;
    this.rectangleSelectionStart = null;
    this.rectangleSelectionEnd = null;
    this.clearSelectionRectangle();
  }

  // ===== MULTI-POINT DRAG METHODS =====

  handleMultiPointDrag(): void {
    const intersectionPoint = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.plane, intersectionPoint)) return;

    const currentShape = this.shapes[this.currentShapeIndex];
    if (!currentShape) return;

    if (this.selectedPointMesh && this.dragStartWorldPos && this.currentPointIndex !== -1 && this.multiSelectDragStart[this.currentPointIndex]) {
      let primaryPointNewWorldPos = intersectionPoint.clone().sub(this.dragStartWorldPos);

      // Calculate delta from the primary selected point's original position
      const primaryOriginalPos = this.multiSelectDragStart[this.currentPointIndex];
      let deltaX = primaryPointNewWorldPos.x - primaryOriginalPos.x;
      let deltaY = primaryPointNewWorldPos.y - primaryOriginalPos.y;

      // No snapping for delta, apply snapping to final positions later if needed.

      let canMoveAll = true;
      const newPositions: { [pointIndex: number]: { x: number, y: number } } = {};

      // First, calculate all new positions and check for potential duplicates
      for (const pointIndex of this.selectedPoints) {
        if (pointIndex >= 0 && pointIndex < currentShape.points.length && this.multiSelectDragStart[pointIndex]) {
          const originalPos = this.multiSelectDragStart[pointIndex];
          let targetX = originalPos.x + deltaX;
          let targetY = originalPos.y + deltaY;

          if (this.snap) {
            targetX = Math.round(targetX / this.gridsize) * this.gridsize;
            targetY = Math.round(targetY / this.gridsize) * this.gridsize;
          }
          newPositions[pointIndex] = { x: targetX, y: targetY };

          const TOLERANCE = this.snap ? this.gridsize * 0.1 : 0.001;
          const wouldBeDuplicate = currentShape.points.some((p, idx) => {
            if (this.selectedPoints.includes(idx)) return false; // Skip points in selection
            const distance = Math.sqrt(
              Math.pow(p.cx - targetX, 2) +
              Math.pow(p.cy - targetY, 2)
            );
            return distance < TOLERANCE;
          });

          if (wouldBeDuplicate) {
            canMoveAll = false;
            break;
          }
        }
      }

      if (canMoveAll) {
        this.selectedPoints.forEach(pointIndex => {
          if (newPositions[pointIndex]) {
            currentShape.points[pointIndex].cx = newPositions[pointIndex].x;
            currentShape.points[pointIndex].cy = newPositions[pointIndex].y;

            const pointMesh = this.pointMeshes.find(m =>
              m.userData.shapeIndex === this.currentShapeIndex &&
              m.userData.pointIndex === pointIndex
            );
            if (pointMesh) {
              pointMesh.position.set(newPositions[pointIndex].x, newPositions[pointIndex].y, 0);
            }
          }
        });
        this.updateData();
      }
    }
  }

  ngOnDestroy(): void {
    // Cleanup Three.js objects to prevent memory leaks
    if (this.renderer) {
      this.renderer.dispose();
    }
    if (this.scene) {
      this.scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });
    }
    this.pointMeshes.forEach(mesh => {
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) (mesh.material as THREE.Material).dispose();
    });
    this.clearPreviewLine();
    this.clearStartPointVisual();
    this.clearSnapIndicator();
    this.clearSelectionRectangle();

    this.clearBoundingBox();
    this.clearGhostPoints();
  }

  clearPreviewCircle(): void {
    if (this.previewCircle) {
      this.scene.remove(this.previewCircle);
      if (this.previewCircle.geometry) this.previewCircle.geometry.dispose();
      if (this.previewCircle.material) (this.previewCircle.material as THREE.Material).dispose();
      this.previewCircle = null;
    }
  }

  createPreviewCircle(center: ShapePoint, radius: number): void {
    this.clearPreviewCircle();

    // Create circle geometry
    const segments = 64; // Number of segments for smooth circle
    const points: THREE.Vector3[] = [];

    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const x = center.cx + Math.cos(angle) * radius;
      const y = center.cy + Math.sin(angle) * radius;
      points.push(new THREE.Vector3(x, y, 0.1)); // Slightly above other lines
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: 0x00ff00, // Bright green for preview
      opacity: 0.8,
      transparent: true,
      linewidth: 2
    });

    this.previewCircle = new THREE.Line(geometry, material);
    this.scene.add(this.previewCircle);
    this.renderScene();
  }

  updatePreviewCircle(center: ShapePoint, radius: number): void {
    if (this.previewCircle) {
      // Update the circle geometry with new radius
      const segments = 64;
      const points: THREE.Vector3[] = [];

      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = center.cx + Math.cos(angle) * radius;
        const y = center.cy + Math.sin(angle) * radius;
        points.push(new THREE.Vector3(x, y, 0.1));
      }

      this.previewCircle.geometry.setFromPoints(points);
      this.previewCircle.geometry.attributes.position.needsUpdate = true;
      this.renderScene();
    } else {
      // Create new preview circle if it doesn't exist
      this.createPreviewCircle(center, radius);
    }
  }
}
